import React, { useState, useEffect, useRef } from 'react';
import { format, parseISO, isValid, startOfMonth, endOfMonth, eachDayOfInterval, getDay, isSameDay, addMonths, subMonths } from 'date-fns';
import { de } from 'date-fns/locale';
import { Calendar, ChevronLeft, ChevronRight, X } from 'lucide-react';

const WEEKDAYS = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

/**
 * DatePicker – dark-themed date selector
 * value: YYYY-MM-DD string (or '')
 * onChange: (YYYY-MM-DD | '') => void
 */
export default function DatePicker({
  value = '',
  onChange,
  placeholder = 'Datum wählen',
  className = '',
  'data-testid': testId,
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const [viewDate, setViewDate] = useState(() => {
    if (value) { const d = parseISO(value); if (isValid(d)) return d; }
    return new Date();
  });
  const containerRef = useRef(null);

  // Sync view when value changes externally
  useEffect(() => {
    if (value) { const d = parseISO(value); if (isValid(d)) setViewDate(d); }
  }, [value]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const selectedDate = value && isValid(parseISO(value)) ? parseISO(value) : null;

  const monthStart     = startOfMonth(viewDate);
  const monthEnd       = endOfMonth(viewDate);
  const days           = eachDayOfInterval({ start: monthStart, end: monthEnd });
  const firstWeekday   = (getDay(monthStart) + 6) % 7; // Mon=0

  const handleDayClick = (day) => {
    onChange(format(day, 'yyyy-MM-dd'));
    setOpen(false);
  };

  const clear = (e) => {
    e.stopPropagation();
    onChange('');
  };

  return (
    <div ref={containerRef} className={`relative ${className}`} data-testid={testId}>
      {/* Trigger */}
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen(o => !o)}
        className="input-grow w-full flex items-center gap-2 text-left min-h-[38px]"
        style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
      >
        <Calendar size={14} className="text-emerald-500/60 flex-shrink-0" />
        <span className={`flex-1 text-sm ${selectedDate ? 'text-emerald-100' : 'text-emerald-100/30'}`}>
          {selectedDate ? format(selectedDate, 'dd.MM.yyyy') : placeholder}
        </span>
        {selectedDate && !disabled && (
          <span onClick={clear} className="text-emerald-100/30 hover:text-emerald-100/70 transition-colors p-0.5">
            <X size={12} />
          </span>
        )}
      </button>

      {/* Dropdown */}
      {open && (
        <div
          className="absolute z-50 top-full mt-1 left-0 rounded-xl shadow-2xl p-3"
          style={{
            background: '#0A120D',
            border: '1px solid #1B2A21',
            minWidth: '268px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.6)',
          }}
          data-testid={testId ? `${testId}-dropdown` : undefined}
        >
          {/* Month nav */}
          <div className="flex items-center justify-between mb-2 px-1">
            <button
              type="button"
              onClick={() => setViewDate(d => subMonths(d, 1))}
              className="p-1.5 rounded-lg text-emerald-100/50 hover:text-emerald-100 hover:bg-[#111C15] transition-colors"
            >
              <ChevronLeft size={16} />
            </button>
            <span className="text-sm font-semibold text-emerald-100 capitalize">
              {format(viewDate, 'MMMM yyyy', { locale: de })}
            </span>
            <button
              type="button"
              onClick={() => setViewDate(d => addMonths(d, 1))}
              className="p-1.5 rounded-lg text-emerald-100/50 hover:text-emerald-100 hover:bg-[#111C15] transition-colors"
            >
              <ChevronRight size={16} />
            </button>
          </div>

          {/* Weekday headers */}
          <div className="grid grid-cols-7 mb-1">
            {WEEKDAYS.map(d => (
              <div key={d} className="text-center text-xs text-emerald-100/30 py-1 font-medium">{d}</div>
            ))}
          </div>

          {/* Day grid */}
          <div className="grid grid-cols-7 gap-0.5">
            {Array.from({ length: firstWeekday }, (_, i) => (
              <div key={`empty-${i}`} />
            ))}
            {days.map(day => {
              const isSel   = selectedDate && isSameDay(day, selectedDate);
              const isToday = isSameDay(day, new Date());
              return (
                <button
                  key={day.toISOString()}
                  type="button"
                  onClick={() => handleDayClick(day)}
                  className="aspect-square flex items-center justify-center text-xs rounded-lg transition-all"
                  style={{
                    background: isSel
                      ? '#10B981'
                      : isToday
                      ? 'rgba(16,185,129,0.12)'
                      : 'transparent',
                    color: isSel
                      ? '#fff'
                      : isToday
                      ? '#34D399'
                      : 'rgba(209,250,229,0.7)',
                    fontWeight: isSel || isToday ? 600 : 400,
                    border: isToday && !isSel ? '1px solid rgba(16,185,129,0.3)' : '1px solid transparent',
                  }}
                  onMouseEnter={e => { if (!isSel) e.currentTarget.style.background = '#111C15'; }}
                  onMouseLeave={e => {
                    if (!isSel) e.currentTarget.style.background = isToday ? 'rgba(16,185,129,0.12)' : 'transparent';
                  }}
                >
                  {format(day, 'd')}
                </button>
              );
            })}
          </div>

          {/* Today shortcut */}
          <div className="mt-2 pt-2 border-t" style={{ borderColor: '#1B2A21' }}>
            <button
              type="button"
              onClick={() => { onChange(format(new Date(), 'yyyy-MM-dd')); setOpen(false); }}
              className="w-full text-center text-xs text-emerald-400 hover:text-emerald-300 py-1 transition-colors"
            >
              Heute
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
